import { Component, Inject, OnDestroy, OnInit, Signal, signal, ChangeDetectionStrategy } from '@angular/core';
import { MatLineModule } from '@angular/material/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AccountGroup, Constraint, ProjectionFilter } from '@vendasta/account-group';
import { BusinessSelectorDialogService } from '@vendasta/businesses';
import { UntypedFormControl } from '@angular/forms';
import { Subscription, Observable, startWith, tap, combineLatest, map } from 'rxjs';
import { InboxAccountContactService } from './inbox-account-contact.service';
import { Router } from '@angular/router';
import { FormatAddressToStringPipe } from '@vendasta/businesses';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { InViewportModule } from 'ng-in-viewport';
import { LexiconModule } from '@galaxy/lexicon';
import { ContactInfo } from './contact-info-class';

export interface DialogData {
  partnerId: string;
  marketId: string | string[];
  projectionFilterOverride?: ProjectionFilter;
  hideCreateBusiness?: boolean;
  createBusinessPath: string;
  createContactPath: string;
}

enum SELECT_OPTIONS {
  ACCOUNTS = 1,
  CONTACTS = 2,
}

export interface InboxAccountContactOutput {
  business?: AccountGroup;
  contact?: ContactInfo;
}

@Component({
  selector: 'inbox-account-contact',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InViewportModule,
    ReactiveFormsModule,
    LexiconModule,
    CommonModule,
    MatChipsModule,
    GalaxyFormFieldModule,
    MatLineModule,
    FormatAddressToStringPipe,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatListModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
  ],
  templateUrl: './inbox-account-contact.component.html',
  styleUrl: './inbox-account-contact.component.scss',
})
export class InboxAccountContactComponent implements OnInit, OnDestroy {
  private readonly subscriptions: Subscription[] = [];
  protected readonly searchFormField = new UntypedFormControl('');
  protected loadingAccounts$: Observable<boolean>;
  protected loadingContacts$: Observable<boolean>;
  protected business$: Observable<AccountGroup[]>;
  protected contacts$: Observable<ContactInfo[]>;
  protected hasMoreAccounts$: Observable<boolean>;
  protected hasMoreContacts$: Observable<boolean>;
  protected loadingFirstPageAccounts: Signal<boolean>;
  protected loadingFirstPageContacts: Signal<boolean>;
  public selectedOption = signal(SELECT_OPTIONS.ACCOUNTS);
  public businessesAndContacts$: Observable<{
    businesses: AccountGroup[];
    contacts: ContactInfo[];
  }>;

  SELECT_OPTIONS = SELECT_OPTIONS;

  constructor(
    private readonly dialogRef: MatDialogRef<InboxAccountContactComponent>,
    private readonly router: Router,
    private readonly businessSelectorDialogService: BusinessSelectorDialogService,
    private readonly inboxAccountContactService: InboxAccountContactService,
    @Inject(MAT_DIALOG_DATA) protected readonly data: DialogData,
  ) {
    this.businessSelectorDialogService.data = this.data;
    this.inboxAccountContactService.partnerId = this.data.partnerId;
    this.loadingAccounts$ = this.businessSelectorDialogService.loading$;
    this.loadingContacts$ = this.inboxAccountContactService.loading$;
    this.business$ = this.businessSelectorDialogService.business$;
    this.contacts$ = this.inboxAccountContactService.contacts$;
    this.hasMoreAccounts$ = this.businessSelectorDialogService.hasMore$;
    this.hasMoreContacts$ = this.inboxAccountContactService.hasMoreContacts$;
    this.loadingFirstPageAccounts = this.businessSelectorDialogService.loadingFirstPage;
    this.loadingFirstPageContacts = this.inboxAccountContactService.loadingFirstPage;

    this.businessesAndContacts$ = combineLatest([this.business$, this.contacts$]).pipe(
      map(([businesses, contacts]) => ({ businesses, contacts })),
    );
  }

  ngOnInit(): void {
    this.businessSelectorDialogService.initBusinesses();
    this.inboxAccountContactService.initContacts();

    this.subscriptions.push(
      this.searchFormField.valueChanges
        .pipe(
          startWith(''),
          tap(() => {
            this.businessSelectorDialogService.searchFormFieldValue = this.searchFormField.value;
            this.inboxAccountContactService.searchFormFieldValue = this.searchFormField.value;
          }),
        )
        .subscribe(),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  loadMoreAccounts(inViewportEvent: any): void {
    if (inViewportEvent.visible) {
      this.businessSelectorDialogService.loadMore = true;
    }
  }

  loadMoreContacts(inViewportEvent: any): void {
    if (inViewportEvent.visible) {
      this.inboxAccountContactService.loadMoreContacts = true;
    }
  }

  selectOption(option: SELECT_OPTIONS): void {
    this.selectedOption.set(option);
  }

  selectBusiness(accountGroup: AccountGroup): void {
    this.dialogRef.close({ business: accountGroup });
  }

  selectContact(contact: ContactInfo): void {
    this.dialogRef.close({ contact: contact });
  }

  createBusiness(): void {
    this.dialogRef.close();
    this.router.navigateByUrl(this.data.createBusinessPath);
  }

  createContact(): void {
    this.dialogRef.close();
    this.router.navigateByUrl(this.data.createContactPath);
  }

  getPartnerIdfromConstraints(constraints: Constraint[]): string {
    return constraints.find((element) => element?.foreignKey?.kind === 'PartnerAccountGroup')?.foreignKey?.key || '';
  }

  close(): void {
    this.dialogRef.close();
  }
}
